/**
 * Bündelt alle Domänenobjekte zu Bürgschafen. Dabei gibt es Referenzen zu Partner (z.B. wenn es um Ansprechpartner
 * geht) und zu Dokument (z.B. zu Template).
 */
import i18n from '@/lang/i18nPlugin';
import type { Postadresse } from './Partner';
import { createEmptyPostadresse } from './Partner';
import type { Baurecht } from './DokumentBGT';
import type {
  Ansprechpartner,
  AuftraggeberTyp,
  BuergschaftsLieferkanal,
  Bürgschaftsart,
} from './Bürgschaft';
import { createEmptyAnsprechpartner } from './Bürgschaft';

/** Die Bürgschaft - zentrales Business-Objekt der Bürgschaftservice-Plattform. Bedarf eigentlich keiner Erklärung */
export interface Buergschaftsdaten {
  voraussichtlichesAblaufdatum: string;
  buergschaftsart: Bürgschaftsart;
  befristungsdatum?: string;
  // Aus dem Backend bekommen wir diesen Key, aber das Frontend hat bisher mit der lower Case Variante gearbeitet
  befristungsDatum?: string;
  befristung?: boolean;
  buergschaftsBetrag: string; // Hier wäre ein spezieller Datentyp gut
  waehrungskennzeichen?: string;
  vertragsnummer: string;
  vertragsdatum: string;
  bauleistung: string;
  bauvorhaben?: Bauvorhaben;
  buergschaftsauftragstatus?: Buergschaftsauftragstatus;
  baurecht?: Baurecht;
  bausumme?: string;
  bausummeprozent?: string;
  buergschaftsId?: string;
  buergschaftsIdBuerge?: string;
  internerVermerk?: string;
  ausstellungsdatumBuerge?: string;
  zahlungAufErstesAnfordern?: boolean;
}

export interface BuergschaftsauftragMitteilungDetails {
  buergeId: string;
  buergeName: string;
  status?: string;
  lieferkanal?: BuergschaftsLieferkanal;
  eingangszeitpunkt: string;
  auftraggeberName?: string;
  auftragnehmerName?: string;
  bauleistung?: string;
  bauadresse?: Postadresse;
  bauvorhaben: string;
  buergschaftId?: string;
}

export interface BuergschaftsauftragDetailsFuerAufgabenListe {
  buergschaftsauftragId?: number;
  status?: string;
}

/** Status, die eine Bürgschaft durchläuft */
export enum BuergschaftsauftragstatusFilter {
  /** UNVOLLSTAENDIG */
  UNVOLLSTAENDIG = 'UNVOLLSTAENDIG',

  /** SCHWEBE */
  SCHWEBE = 'SCHWEBE',

  /** IN PRUEFUNG */
  IN_PRUEFUNG = 'IN_PRUEFUNG',

  /** AUSGESTELLT */
  AUSGESTELLT = 'AUSGESTELLT',

  /** ABGELEHNT */
  ABGELEHNT = 'ABGELEHNT',

  /** STORNIERT */
  STORNIERT = 'STORNIERT',
}

/** Status, die eine Bürgschaft durchläuft */
export enum Buergschaftsauftragstatus {
  /** UNVOLLSTAENDIG */
  UNVOLLSTAENDIG = 'UNVOLLSTAENDIG',

  /** IN PRUEFUNG */
  IN_PRUEFUNG = 'IN_PRUEFUNG',

  /** SCHWEBE */
  SCHWEBE = 'SCHWEBE',

  /** AUSGESTELLT */
  AUSGESTELLT = 'AUSGESTELLT',

  /** ABGELEHNT */
  ABGELEHNT = 'ABGELEHNT',

  /** STORNIERT */
  STORNIERT = 'STORNIERT',
}

/** Status, die eine Bürgschaft durchläuft */
export enum Lieferkanal {
  /** TRUSTLOG */
  TRUSTLOG = 'TRUSTLOG',

  /** GASTLOG */
  GASTLOG = 'GASTLOG',

  /** PDF */
  PDF = 'PDF',

  /** POST */
  POST = 'POST',
}

/** Map, die den Bürgschaftsantragstatus auf einen Text für die Anzeige abbildet - für die Anzeige in Tabellen */
export const BuergschaftsauftragStatusAnzeigetextTabelleBürgeMap: Map<
  Buergschaftsauftragstatus,
  string
> = new Map([
  [
    Buergschaftsauftragstatus.STORNIERT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[0]'),
  ],
  [
    Buergschaftsauftragstatus.AUSGESTELLT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[1]'),
  ],
  [
    Buergschaftsauftragstatus.ABGELEHNT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[2]'),
  ],
  [
    Buergschaftsauftragstatus.UNVOLLSTAENDIG,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[3]'),
  ],
  [
    Buergschaftsauftragstatus.SCHWEBE,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[4]'),
  ],
  [
    Buergschaftsauftragstatus.IN_PRUEFUNG,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[5]'),
  ],
]);

/** Map, die den Lieferkanal in seiner Darstellung */
export const LieferkanalMap: Map<Lieferkanal, string> = new Map([
  [
    Lieferkanal.TRUSTLOG,
    'Da der Auftraggeber auch bei Trustlog registriert ist, wird die Bürgschaft digital in Trustlog ausgestellt.',
  ],
  [
    Lieferkanal.GASTLOG,
    'Zukünftig: teilen Sie die Bürgschaft mit dem Auftraggeber in Trustlog und sparen Sie sich das Versenden des PDFs.',
  ],
  [
    Lieferkanal.PDF,
    'Sie übersenden dem Auftraggeber das Original der Bürgschaft im PDF-Format, sobald die Bürgschaft durch den Bürgen ausgestellt wurde. Sie werden über die Ausstellung informiert und finden die Bürgschaft dann unter dem Navigationspunkt "Bürgschaften"',
  ],
  [Lieferkanal.POST, 'Die Bürgschaft wird durch den Bürgen per Post bereitgestellt.'],
]);

/** Der Auftragnehmer ist der Schuldner im Bürgschaftsvertrag */
export interface Auftragnehmer {
  auftragnehmerId?: string;
  fremdId: string;
  name: string;
  postadresse: Postadresse;
  anzahlToechter?: number;
  ansprechpartner?: Ansprechpartner;
}

export interface BuergeDaten {
  ablehnungsgrund?: string;
  ansprechpartner?: Ansprechpartner;
}

/** Der Auftragnehmer ist der Schuldner im Bürgschaftsvertrag */
export interface Bauvorhaben {
  adresse?: Postadresse;
  freitext?: string;
}

export function createEmptyVersandadresse(): Versandadresse {
  return {
    name: '',
    zustellanweisung: '',
    postadresse: createEmptyPostadresse(),
  };
}

export function createEmptyAuftraggeber(): Auftraggeber {
  return {
    auftraggeberId: '',
    name: '',
    postadresse: createEmptyPostadresse(),
    versandadresse: createEmptyVersandadresse(),
    oeffentlicherAuftraggeber: false,
    vertretungsformel: [],
    partnertyp: null,
  };
}

/** Der Auftragnehmer ist der Schuldner im Bürgschaftsvertrag */
export interface Versandadresse {
  name: string;
  zustellanweisung: string;
  postadresse: Postadresse;
}

/** Der Auftraggeber ist der Gläubiger im Bürgschaftsvertrag */
export interface Auftraggeber {
  auftraggeberId: string;
  name: string;
  postadresse: Postadresse;
  versandadresse?: Versandadresse | null;
  oeffentlicherAuftraggeber?: boolean;
  vertretungsformel?: string[];
  partnertyp: AuftraggeberTyp;
  ansprechpartner?: Ansprechpartner | null;
}

export function createEmptyAuftragnehmer(): Auftragnehmer {
  return {
    fremdId: '',
    name: '',
    postadresse: createEmptyPostadresse(),
  };
}

/** Der Bürge der Bürgschaft */
export interface Buerge {
  id: string;
}

/** Die Template-ID der Bürgschaft */
export interface Template {
  buergschaftstemplateId: string;
  recht?: string;
}

export function createEmptyBuerge(): Buerge {
  return {
    id: '',
  };
}

export interface Buergschaftsauftrag {
  auftrag?: Auftragsdaten;
  eingangszeitpunkt?: string;
  buergschaftsdaten: Buergschaftsdaten;
  auftragnehmer: Auftragnehmer;
  auftraggeber: Auftraggeber;
  buergeId?: string;
  buergschaftstext?: Template;
}

export function createEmptyBuergschaftsdaten(): Buergschaftsdaten {
  return {
    voraussichtlichesAblaufdatum: '',
    buergschaftsBetrag: '',
    buergschaftsart: undefined,
    vertragsdatum: '',
    vertragsnummer: '',
    befristungsdatum: '',
    bauleistung: '',
    bauvorhaben: {
      adresse: createEmptyPostadresse(),
    },
    ausstellungsdatumBuerge: '',
    zahlungAufErstesAnfordern: false,
  };
}

export function createEmptyBuergschaftsauftrag(): Buergschaftsauftrag {
  return {
    auftrag: null,
    eingangszeitpunkt: null,
    buergschaftsdaten: createEmptyBuergschaftsdaten(),
    auftragnehmer: createEmptyAuftragnehmer(),
    auftraggeber: createEmptyAuftraggeber(),
    buergeId: '',
    buergschaftstext: null,
  };
}

export function createEmptyBuergendaten(): BuergeDaten {
  return {
    ablehnungsgrund: '',
    ansprechpartner: createEmptyAnsprechpartner(),
  };
}

/** Die Template-ID der Bürgschaft */
export interface Auftragsdaten {
  auftragsId?: number;
  status: string;
  art: string;
  alteBuergschaftsIdBuerge: string;
  eingangszeitpunkt: string;
  lieferkanal?: string;
}

export function mappeBuergschaftsdaten(buergschaftsdaten: Buergschaftsdaten) {
  return {
    voraussichtlichesAblaufdatum: buergschaftsdaten.voraussichtlichesAblaufdatum,
    buergschaftsart: buergschaftsdaten.buergschaftsart,
    befristungsdatum: buergschaftsdaten.befristungsdatum || buergschaftsdaten.befristungsDatum,
    befristung: buergschaftsdaten.befristung,
    waehrungskennzeichen: buergschaftsdaten.waehrungskennzeichen,
    vertragsnummer: buergschaftsdaten.vertragsnummer,
    vertragsdatum: buergschaftsdaten.vertragsdatum,
    bauleistung: buergschaftsdaten.bauleistung,
    bauvorhaben: buergschaftsdaten.bauvorhaben,
    buergschaftsauftragstatus: buergschaftsdaten.buergschaftsauftragstatus,
    baurecht: buergschaftsdaten.baurecht,
    bausumme: buergschaftsdaten.bausumme,
    bausummeprozent: buergschaftsdaten.bausummeprozent,
    internerVermerk: buergschaftsdaten.internerVermerk,
    ausstellungsdatumBuerge: buergschaftsdaten.ausstellungsdatumBuerge,
    // in Zweitprüfung-Prozess brauchen wir einheitliche labels wie Bürgschaft->BuergschaftGrunddaten
    buergschaftsbetrag: buergschaftsdaten.buergschaftsBetrag,
    buergschaftId: buergschaftsdaten.buergschaftsId,
    buergschaftIdBuerge: buergschaftsdaten.buergschaftsIdBuerge,
  };
}
